/**
 * Cercas desenhadas no mapa.
 *
 * Lista numa tabela não responde a pergunta que o operador faz — "o ônibus está
 * dentro ou fora?". Sobre o mapa, responde sozinha.
 *
 * Cerca **inativa** aparece apagada e tracejada em vez de sumir: ela existe no
 * cadastro, e escondê-la faria alguém recriar uma que já está lá.
 */
import { Fragment, useState } from "react";
import { Circle, Marker, Polygon, Polyline, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import {
  DEFAULT_COLOR,
  centroidOf,
  midpointOfPath,
  strokeForWidth,
  toLeaflet,
  toLeafletLine,
} from "../lib/geofence.ts";
import type { Geofence } from "../lib/schemas.ts";

// A conversão [lon,lat] -> [lat,lon] mora em `lib/geofence.ts`, e só lá: este
// arquivo tinha uma CÓPIA dela, e as duas estavam erradas do mesmo jeito —
// esperavam `{ coordinates: [anel] }` onde a API manda array puro. Duas cópias
// da mesma conta é duas chances de consertar só uma.

/**
 * Classe de todas as camadas de cerca no mapa.
 *
 * Existe para o teste do navegador poder contar cerca sem confundir com o rastro
 * do veículo — as duas são polilinha desde que a cerca de linha passou a
 * existir. Vai nas `pathOptions`, então vale também com `preferCanvas`, em que
 * não há elemento no DOM para inspecionar.
 */
/**
 * Âncora invisível da etiqueta.
 *
 * O tooltip precisa de uma camada para se prender, e prendê-lo à própria forma
 * ancoraria no centro da CAIXA envolvente — que numa cerca em L ou alongada cai
 * fora dela. Um marcador de tamanho zero no ponto certo resolve, e `interactive:
 * false` o mantém fora do caminho do clique no mapa.
 */
const LABEL_ANCHOR = L.divIcon({
  // A classe não é enfeite: âncora de etiqueta É um marcador do Leaflet, e sem
  // a marca ela entra na contagem dos marcadores de VEÍCULO — quebrou o teste
  // na hora em que a etiqueta fixa nasceu, que é o teste fazendo o trabalho.
  className: "mt-cerca-label",
  html: "",
  iconSize: [0, 0],
  iconAnchor: [0, 0],
});

export const GEOFENCE_CLASS = "mt-cerca";

/**
 * Opacidade do preenchimento.
 *
 * Era 0,1, e sobre imagem de satélite isso é indistinguível de nada: a cerca
 * aparecia só pelo contorno de 2 px. 0,28 deixa a área visível e ainda deixa
 * ler a rua por baixo, que é o ponto de ser translúcida — uma cerca opaca
 * esconderia justamente o que ela delimita.
 *
 * A inativa fica em 0,08: continua no mapa, como manda a regra de nomear a
 * ausência, mas não disputa atenção com as que valem.
 */
const FILL_ACTIVE = 0.28;
const FILL_INACTIVE = 0.08;

export function GeofenceLayer({ geofences }: { geofences: readonly Geofence[] }) {
  // O zoom entra em estado por causa da cerca de LINHA: a largura dela é em
  // metros e a espessura do traço do Leaflet é em pixels, então a conversão
  // depende do zoom. Sem redesenhar, a faixa mudaria de largura aparente a cada
  // aproximação — e o operador não teria como conferir os metros que cadastrou.
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());
  useMapEvents({
    zoomend() {
      setZoom(map.getZoom());
    },
  });

  return (
    <>
      {geofences.map((cerca) => {
        // A cor é a do CADASTRO (migration 0026); sem ela, a padrão do painel.
        // Com vinte cercas sobrepostas todas roxas não se distingue um terminal
        // de uma via — e foi para isso que o campo de cor existe.
        //
        // Ativa e inativa têm a MESMA matiz: cerca inativa continua sendo
        // cerca, e o que a distingue é o traço e a intensidade, não outra cor.
        // Trocar a cor da inativa desfaria a escolha de quem cadastrou.
        //
        // O cinza que eu usava aqui (#94a3b8) é EXATAMENTE a cor do status
        // "desligado" do rastro. Duas coisas sem relação com a mesma cor no
        // mesmo mapa confunde quem lê, e confundiu até o teste.
        const cor = cerca.color ?? DEFAULT_COLOR;
        const estilo = {
          // Marca a camada como CERCA, e não é enfeite: o rastro do veículo
          // também é polilinha, e distinguir os dois por estrutura (`_latlngs`
          // plano x array de anéis) deixou de funcionar quando a cerca de linha
          // passou a existir. O teste do navegador pergunta pela marca em vez
          // de deduzir a resposta.
          className: GEOFENCE_CLASS,
          color: cor,
          weight: 2,
          opacity: cerca.active ? 0.9 : 0.45,
          fillColor: cor,
          fillOpacity: cerca.active ? FILL_ACTIVE : FILL_INACTIVE,
          ...(cerca.active ? {} : { dashArray: "5 5" }),
        };
        /**
         * O nome da cerca, FIXO no centro dela.
         *
         * Era tooltip de hover, e hover não existe em tela de toque — e mesmo no
         * mouse, descobrir o nome de uma cerca exigia adivinhar onde apontar. É
         * a mesma correção que a etiqueta do veículo recebeu em 28/10.
         *
         * `direction="center"` põe o texto no ponto de ancoragem da camada: no
         * círculo é o centro, no polígono é o centroide de área e na linha é o
         * meio do caminho — todos calculados abaixo, porque o ponto que o
         * Leaflet usaria sozinho é o centro da CAIXA, que numa cerca torta cai
         * fora dela.
         *
         * A cor vem do cadastro (migration 0029) e é separada da cor da forma:
         * traço amarelo se distingue das vizinhas, texto amarelo sobre satélite
         * não se lê.
         */
        const rotulo = (
          <Tooltip permanent direction="center" opacity={1} className="geofence-label">
            <span style={cerca.label_color ? { color: cerca.label_color } : undefined}>
              {cerca.name}
              {cerca.active ? "" : " (inativa)"}
            </span>
          </Tooltip>
        );

        if (cerca.kind === "circle" && cerca.center_lat != null && cerca.center_lon != null) {
          return (
            <Fragment key={cerca.id}>
              <Circle
                center={[cerca.center_lat, cerca.center_lon]}
                radius={cerca.radius_m ?? 0}
                pathOptions={estilo}
              />
              <Marker position={[cerca.center_lat, cerca.center_lon]} icon={LABEL_ANCHOR} interactive={false}>
                {rotulo}
              </Marker>
            </Fragment>
          );
        }

        if (cerca.kind === "line") {
          // O traçado aceita DOIS vértices; `toLeaflet` exigiria três e a via
          // reta sumiria do mapa sem erro nenhum.
          const tracado = toLeafletLine(cerca.polygon);
          if (!tracado || !cerca.width_m) return null;
          const meio = midpointOfPath(tracado);
          return (
            <Fragment key={cerca.id}>
            <Polyline
              positions={tracado}
              pathOptions={{
                className: GEOFENCE_CLASS,
                color: estilo.color,
                // A espessura é a LARGURA em metros convertida para pixels no
                // zoom atual: é o que faz uma faixa de 20 m parecer 20 m. Com
                // espessura fixa, 20 m e 200 m desenhariam igual.
                weight: strokeForWidth(cerca.width_m, tracado[0]![0], zoom),
                opacity: cerca.active ? 0.35 : 0.15,
                lineCap: "butt",
                lineJoin: "round",
                ...(cerca.active ? {} : { dashArray: "5 5" }),
              }}
            />
            {meio ? <Marker position={meio} icon={LABEL_ANCHOR} interactive={false}>{rotulo}</Marker> : null}
            </Fragment>
          );
        }

        const anel = toLeaflet(cerca.polygon);
        if (!anel) return null;
        // A etiqueta vai num marcador invisível no centroide, e não presa ao
        // polígono: o Leaflet ancoraria o tooltip no centro da CAIXA
        // envolvente, que num L ou num polígono alongado cai fora da cerca.
        const centro = centroidOf(anel);
        return (
          <Fragment key={cerca.id}>
            <Polygon positions={anel} pathOptions={estilo} />
            {centro ? <Marker position={centro} icon={LABEL_ANCHOR} interactive={false}>{rotulo}</Marker> : null}
          </Fragment>
        );
      })}
    </>
  );
}
